import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { hy, type Dictionary } from "./locales/hy";
import { ru } from "./locales/ru";
import { en } from "./locales/en";

export type Locale = "hy" | "ru" | "en";

export const LOCALES: readonly Locale[] = ["hy", "ru", "en"] as const;
export const DEFAULT_LOCALE: Locale = "hy";
export const LOCALE_STORAGE_KEY = "gita-locale";

const DICTIONARIES: Record<Locale, Dictionary> = { hy, ru, en };

export const isLocale = (value: unknown): value is Locale =>
  typeof value === "string" && (LOCALES as readonly string[]).includes(value);

export const getDictionary = (locale: Locale): Dictionary => DICTIONARIES[locale] ?? hy;

const readInitialLocale = (): Locale => {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  try {
    const fromQuery = new URLSearchParams(window.location.search).get("lang");
    if (isLocale(fromQuery)) {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, fromQuery);
      return fromQuery;
    }
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    /* storage unavailable (private mode, etc.) */
  }
  return DEFAULT_LOCALE;
};

interface LanguageContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Dictionary;
  locales: readonly Locale[];
  dictionaries: Record<Locale, Dictionary>;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);

  const setLocale = useCallback((next: Locale) => {
    if (!isLocale(next)) return;
    setLocaleState(next);
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const dict = getDictionary(locale);
    document.documentElement.lang = locale;
    const og = document.querySelector<HTMLMetaElement>('meta[property="og:locale"]');
    if (og) og.content = dict.meta.ogLocale;
    const langMeta = document.querySelector<HTMLMetaElement>('meta[http-equiv="content-language"]');
    if (langMeta) langMeta.content = locale;
  }, [locale]);

  const value = useMemo<LanguageContextValue>(
    () => ({ locale, setLocale, t: getDictionary(locale), locales: LOCALES, dictionaries: DICTIONARIES }),
    [locale, setLocale],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useI18n = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Allow usage outside the provider (tests, isolated renders) with the default dictionary.
    return {
      locale: DEFAULT_LOCALE,
      setLocale: () => undefined,
      t: hy,
      locales: LOCALES,
      dictionaries: DICTIONARIES,
    };
  }
  return ctx;
};
